import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useOpen } from 'hooks/use-open';

import { buildNonModeledRows } from '../helpers/build-non-modeled-rows';
import { buildOverviewRows } from '../helpers/build-overview-rows';
import {
  formatMetricValue,
  formatMetricValueParts,
} from '../helpers/results-format';
import {
  DEFAULT_RESULTS_TAB,
  NET_COMPREHENSIVE_FEE_KEY_PREFIX,
  TOTAL_FIRST_YEAR_CARDS,
} from '../results-step.constants';

import {
  useAidResults,
  useAidResultsCoa,
  useDownloadAidResultsDataset,
} from '../../../hooks/use-aid-results';
import { useAidModel } from '../../../hooks/use-aid-model-details';
import { useCompareEstimates } from './use-compare-estimates';

import { useCurrentUser } from 'hooks/api/use-current-user';
import {
  clearPerUserFlag,
  readPerUserFlag,
  setPerUserFlag,
} from 'components/shared/side-menu/helpers/per-user-local-storage';

import type {
  AidModelCoaDto,
  AidModelDto,
  AidModelResultRowDto,
} from 'api/generated/schemas';
import type {
  MetricCardData,
  MetricCardLineDefinition,
  MetricLineData,
  NonModeledRow,
  OverviewRow,
  ResultsTabValue,
} from '../results-step.types';
import type { UseCompareEstimatesResult } from './use-compare-estimates';

interface UseResultsStepParams {
  clientId?: string | number;
  previewTotalFirstYear?: AidModelResultRowDto;
}

interface UseResultsStepResult {
  modelId: string | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage: string;
  hasData: boolean;
  activeTab: ResultsTabValue;
  setActiveTab: (tab: ResultsTabValue) => void;
  showNetComprehensiveFee: boolean;
  setShowNetComprehensiveFee: (value: boolean) => void;
  metricCards: MetricCardData[];
  overviewRows: OverviewRow[];
  nonModeledRows: NonModeledRow[];
  showModeledActualYear: boolean;
  setShowModeledActualYear: (value: boolean) => void;
  showLastYear: boolean;
  setShowLastYear: (value: boolean) => void;
  isCoaOpen: boolean;
  openCoa: () => void;
  closeCoa: () => void;
  coaGroups: AidModelCoaDto[];
  isCoaLoading: boolean;
  entryTerm: number | undefined;
  actualYearLabel: string;
  compareEstimates: UseCompareEstimatesResult;
  downloadDataset: () => void;
  isDownloadingDataset: boolean;
  canDownloadDataset: boolean;
}

const readCardValue = (
  row: AidModelResultRowDto | undefined,
  field: keyof AidModelResultRowDto | null
): number | null => {
  const raw = field ? row?.[field] : null;

  return typeof raw === 'number' ? raw : null;
};

interface BuildMetricCardsParams {
  estimated: AidModelResultRowDto | undefined;
  actual: AidModelResultRowDto | undefined;
  original: AidModelResultRowDto | undefined;
  showNetComprehensiveFee: boolean;
}

const buildMetricLine = (
  definition: MetricCardLineDefinition,
  estimated: AidModelResultRowDto | undefined,
  original: AidModelResultRowDto | undefined
): MetricLineData => ({
  key: definition.key,
  label: definition.label,
  value: formatMetricValueParts(
    readCardValue(estimated, definition.field),
    definition.kind
  ),
  originalValue: original
    ? formatMetricValue(
        readCardValue(original, definition.field),
        definition.kind
      )
    : undefined,
  originalTooltip: definition.originalTooltip,
});

const buildMetricCards = ({
  estimated,
  actual,
  original,
  showNetComprehensiveFee,
}: BuildMetricCardsParams): MetricCardData[] =>
  TOTAL_FIRST_YEAR_CARDS.map((card) => {
    const primaryLine: MetricLineData = {
      ...buildMetricLine(card, estimated, original),
      actualValue: formatMetricValue(
        readCardValue(actual, card.field),
        card.kind
      ),
    };

    return {
      key: card.key,
      lines:
        card.netComprehensiveFee
          ? [
              primaryLine,
              {
                ...buildMetricLine(
                  card.netComprehensiveFee,
                  estimated,
                  original
                ),
                isCollapsed: !showNetComprehensiveFee,
              },
            ]
          : [primaryLine],
    };
  });

const buildActualYearLabel = (entryTerm: number | undefined): string =>
  entryTerm ? `Actual ${entryTerm}` : 'Actual';

export const useResultsStep = ({
  clientId: clientIdProp,
  previewTotalFirstYear,
}: UseResultsStepParams = {}): UseResultsStepResult => {
  const { clientId: clientIdParam, modelId } = useParams();
  const clientId = clientIdProp ?? clientIdParam ?? '';
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';
  // Mirrors AidDatasetDownloadController's @Secured roles (SUPER_ADMIN, FD_PRINCIPAL,
  // FD_ANALYST, REPORT_ADMIN) — client-facing USER/INTERNAL_USER roles must not see the button.
  const canDownloadDataset = !!(
    currentUser?.superAdmin ||
    currentUser?.fdPrincipal ||
    currentUser?.fdAnalyst ||
    currentUser?.reportAdmin
  );

  const [activeTab, setActiveTab] =
    useState<ResultsTabValue>(DEFAULT_RESULTS_TAB);
  const storedNetComprehensiveFee = useMemo(
    () => !!userId && readPerUserFlag(NET_COMPREHENSIVE_FEE_KEY_PREFIX, userId),
    [userId]
  );
  const [netComprehensiveFeeOverride, setNetComprehensiveFeeOverride] =
    useState<boolean>();
  const showNetComprehensiveFee =
    netComprehensiveFeeOverride ?? storedNetComprehensiveFee;
  const setShowNetComprehensiveFee = (value: boolean) => {
    setNetComprehensiveFeeOverride(value);

    if (!userId) return;

    if (value) {
      setPerUserFlag(NET_COMPREHENSIVE_FEE_KEY_PREFIX, userId);
    } else {
      clearPerUserFlag(NET_COMPREHENSIVE_FEE_KEY_PREFIX, userId);
    }
  };
  const [showModeledActualYear, setShowModeledActualYear] = useState(false);
  const [showLastYear, setShowLastYear] = useState(false);
  const {
    isOpen: isCoaOpen,
    handleOpen: openCoa,
    handleClose: closeCoa,
  } = useOpen();

  const resultsQuery = useAidResults({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId,
  });

  const coaQuery = useAidResultsCoa({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId && isCoaOpen,
  });

  const downloadDatasetMutation = useDownloadAidResultsDataset();
  const downloadDataset = () => {
    if (!userId || !modelId) return;

    downloadDatasetMutation.mutate({ userId, modelId });
  };

  const modelQuery = useAidModel({
    userId,
    clientId,
    modelId: modelId ?? '',
    enabled: !!modelId && !!clientId,
  });

  // TODO(ts-migration): useAidModel returns a loosely-typed response; narrow when the hook is properly typed
  const modelData = modelQuery.data as AidModelDto | undefined;

  const compareEstimates = useCompareEstimates({
    userId,
    clientId,
    modelId,
    currentModelName: modelData?.name,
  });

  const actualResults = resultsQuery.data?.actualResults;
  const estimatedResults = resultsQuery.data?.estimatedResults;

  const metricCards = useMemo(
    () =>
      buildMetricCards({
        estimated: previewTotalFirstYear ?? estimatedResults?.totalFirstYear,
        actual: actualResults?.totalFirstYear,
        original: previewTotalFirstYear
          ? estimatedResults?.totalFirstYear
          : undefined,
        showNetComprehensiveFee,
      }),
    [
      previewTotalFirstYear,
      estimatedResults,
      actualResults,
      showNetComprehensiveFee,
    ]
  );

  const overviewRows = useMemo(
    () => buildOverviewRows(actualResults, estimatedResults),
    [actualResults, estimatedResults]
  );

  const nonModeledRows = useMemo(
    () => buildNonModeledRows(estimatedResults, actualResults, showLastYear),
    [estimatedResults, actualResults, showLastYear]
  );

  return {
    modelId,
    isLoading: resultsQuery.isLoading,
    isFetching: resultsQuery.isFetching,
    isError: resultsQuery.isError,
    errorMessage:
      (resultsQuery.error as { data?: { detail?: string } })?.data?.detail ||
      'Failed to load results for this model.',
    hasData: !!resultsQuery.data,
    activeTab,
    setActiveTab,
    showNetComprehensiveFee,
    setShowNetComprehensiveFee,
    metricCards,
    overviewRows,
    nonModeledRows,
    showModeledActualYear,
    setShowModeledActualYear,
    showLastYear,
    setShowLastYear,
    isCoaOpen,
    openCoa,
    closeCoa,
    coaGroups: coaQuery.data ?? [],
    isCoaLoading: coaQuery.isLoading,
    entryTerm: modelData?.entryTerm,
    actualYearLabel: buildActualYearLabel(modelData?.entryTerm),
    compareEstimates,
    downloadDataset,
    isDownloadingDataset: downloadDatasetMutation.isPending,
    canDownloadDataset,
  };
};
